import {
  FilterGroupingEnum,
  SiteArea,
  SiteAreaFilterInputObject,
  SiteAreasQueryVariables,
  useSiteAreaIdsLazyQuery,
  useSiteAreasQuery,
} from 'graphql-common';
import _get from 'lodash/get';
import { GetUrlParams } from '@lib/enums/urls';
import createSearchFilters from '@lib/utils/createSearchFilters';
import { ListQueryHookProps, ListQueryResultWithIds } from '../types';

export default function useSiteAreasQueryHook({
  additionalFilters,
  ids,
  paginationParams,
  searchQuery,
  searchQueryFilterSchema = ['name', 'code'],
  siteId,
  skip,
  usePrevious = true,
  fetchPolicy = 'cache-and-network',
  includeFullData = false,
  includeSiteData = false,
  includeSiteIds = false,
}: ListQueryHookProps & {
  siteId?: string;
  includeFullData?: boolean;
  includeSiteData?: boolean;
  includeSiteIds?: boolean;
}): ListQueryResultWithIds<SiteArea> {
  const page = _get(paginationParams, GetUrlParams.Page);
  const perPage = _get(paginationParams, GetUrlParams.PerPage);
  const orderingField = _get(paginationParams, GetUrlParams.OrderingField);
  const orderingDirection = _get(
    paginationParams,
    GetUrlParams.OrderingDirection,
  );
  const siteAreasQueryVariables: SiteAreasQueryVariables = {
    page: Number(page),
    limit: Number(perPage),
    includeFullData,
    includeSiteData,
  };
  if (orderingField && orderingDirection) {
    siteAreasQueryVariables.sorts = {
      [orderingField]: orderingDirection,
    };
  }
  const filters: SiteAreaFilterInputObject[] = [];
  if (searchQuery) {
    const searchFields = searchQueryFilterSchema.map((fieldName) => ({
      fieldName,
    }));
    filters.push(...createSearchFilters(searchQuery, searchFields));
  }
  if (siteId) {
    filters.push({
      siteId: {
        grouping: FilterGroupingEnum.And,
        predicate: { eq: siteId },
        filterGroup: 'siteId',
      },
    });
  }
  if (ids?.length) {
    filters.push({
      id: {
        grouping: FilterGroupingEnum.Or,
        predicate: {
          in: ids,
        },
        filterGroup: 'ids',
      },
    });
  }
  if (additionalFilters?.length) {
    filters.push(...additionalFilters);
  }
  if (filters.length) {
    siteAreasQueryVariables.filters = filters;
  }
  const siteAreasQuery = useSiteAreasQuery({
    fetchPolicy,
    variables: siteAreasQueryVariables,
    skip,
  });
  const [fetchIds] = useSiteAreaIdsLazyQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      filters: siteAreasQueryVariables.filters,
      onlyIds: true,
      includeSiteId: includeSiteIds,
    },
  });
  const { loading, data, previousData, refetch, error } = siteAreasQuery;
  const queryData = usePrevious ? data || previousData : data;
  const firstLoading = loading && previousData === undefined;
  const collection =
    !usePrevious && loading
      ? []
      : (_get(queryData, 'data.collection', []) as SiteArea[]);
  const metadata = _get(queryData, 'data.metadata');
  const totalCount = _get(metadata, 'totalCount', 0);
  const totalPages = _get(metadata, 'totalPages', 1);

  return {
    collection,
    error,
    fetchIds,
    firstLoading,
    loading,
    refetch,
    totalCount,
    totalPages,
  };
}
